import * as React from 'react';
import { Button } from 'react-bootstrap';
import { Translate } from '../../../shared/LanguageService';
import Map from '../../map/Map';
import IWorkingAreaMap from '../../workareas/IWorkingAreaMap';
import ICoordinate from '../../map/ICoordinate';
import Polygon from '../../map/shapes/Polygon';
import Circle from '../../map/shapes/Circle';
import Marker from '../../map/shapes/Marker';
import { merge } from 'lodash';

interface IWorkAreasFormProps {
    photographerId: number;
    workingAreaMaps: Array<IWorkingAreaMap>;
    onMapChange: (maps: IWorkingAreaMap[]) => void;
    setDefaultConfigSe360: (currentWorkingAreaMap: IWorkingAreaMap) => void;
}

interface IState {
    workingAreaMaps: IWorkingAreaMap[];
    currentIndex: number;
}

interface IMapsObject {
    maps: IWorkingAreaMap[];
    marker: Marker;
}

interface IMapChangeHandlers {
    onCircleChange(center: ICoordinate, radius: number): void;
    onMarkerChange(center: ICoordinate): void;
    onPolygonChange(path: ICoordinate[]): void;
}

function getDefaultPolygon(base: ICoordinate): ICoordinate[] {
    return [
        { latitude: base.latitude - 0.05, longitude: base.longitude - 0.1 },
        { latitude: base.latitude + 0.05, longitude: base.longitude - 0.1 },
        { latitude: base.latitude + 0.05, longitude: base.longitude + 0.1 },
        { latitude: base.latitude - 0.05, longitude: base.longitude + 0.1 },
        { latitude: base.latitude - 0.05, longitude: base.longitude - 0.1 }
    ];
}

function setupMap(
    maps: IWorkingAreaMap[],
    index: number,
    handlers: IMapChangeHandlers
): IMapsObject {
    let returnMapsObject = {
        maps: merge([], maps)
    } as IMapsObject;

    if (!returnMapsObject.maps || returnMapsObject.maps.length === 0) {
        const defaultBase: ICoordinate = {
            latitude: 59.329323,
            longitude: 18.068581
        };
        const defaultPath: ICoordinate[] = getDefaultPolygon(defaultBase);

        // Initialize seven maps with Stockholm as
        // configuration
        let workingAreaMaps = [];
        for (let i = 0; i < 7; i++) {
            returnMapsObject.maps.push({
                id: null,
                day: i + 1,
                usePolygon: false,
                useCircle: false,
                base: defaultBase,
                polygon: defaultPath,
                circleCenter: defaultBase,
                circleRadius: 3000
            });
        }
    }

    return returnMapsObject;
}

export class WorkAreasForm extends React.Component<IWorkAreasFormProps, IState> {
    constructor(props: any, context: any) {
        super(props, context);

        const result = setupMap(this.props.workingAreaMaps, 0, this.getMapHandlers());

        this.state = {
            workingAreaMaps: result.maps,
            currentIndex: 0
        };
    }

    UNSAFE_componentWillReceiveProps(nextProps: IWorkAreasFormProps, nextState: any) {
        const workingAreaMaps = Object.assign([], nextProps.workingAreaMaps);

        if (JSON.stringify(this.state.workingAreaMaps) !== JSON.stringify(workingAreaMaps)) {
            var result = setupMap(workingAreaMaps, this.state.currentIndex, this.getMapHandlers());

            if (!nextProps.workingAreaMaps || nextProps.workingAreaMaps.length === 0) {
                this.props.onMapChange(result.maps);
            }

            this.setState({
                ...this.state,
                workingAreaMaps: result.maps,
                polygon: result.polygon,
                circle: result.circle,
                marker: result.marker
            });
        }
    }

    getMapHandlers = () => {
        return {
            onCircleChange: this.onCircleChange,
            onMarkerChange: this.onMarkerChange,
            onPolygonChange: this.onPolygonChange
        };
    };

    onPolygonChange = (path: ICoordinate[]) => {
        this.setState(
            {
                ...this.state,
                workingAreaMaps: this.state.workingAreaMaps.map(
                    (map: IWorkingAreaMap, index: number) => {
                        if (index === this.state.currentIndex) {
                            return merge({}, map, { polygon: path });
                        }
                        return merge({} as IWorkingAreaMap, map);
                    }
                )
            },
            () => {
                this.props.onMapChange(this.state.workingAreaMaps);
            }
        );
    };

    onCircleChange = (center: ICoordinate, radius: number) => {
        this.setState(
            {
                workingAreaMaps: this.state.workingAreaMaps.map(
                    (map: IWorkingAreaMap, index: number) => {
                        if (index === this.state.currentIndex) {
                            return merge(map, {
                                circleCenter: center,
                                circleRadius: Math.round(radius)
                            } as IWorkingAreaMap);
                        }
                        return merge(map, {});
                    }
                )
            },
            () => {
                this.props.onMapChange(this.state.workingAreaMaps);
            }
        );
    };

    onMarkerChange = (center: ICoordinate) => {
        this.setState(
            {
                workingAreaMaps: this.state.workingAreaMaps.map(
                    (map: IWorkingAreaMap, index: number) => {
                        if (index === this.state.currentIndex) {
                            return merge(map, { base: center });
                        }
                        return merge(map, {});
                    }
                )
            },
            () => {
                this.props.onMapChange(this.state.workingAreaMaps);
            }
        );
    };

    createPolygonFromMap = (map: IWorkingAreaMap) => {
        let polygonPath = map.usePolygon && map.polygon ? map.polygon : getDefaultPolygon(map.base);
        return new Polygon(polygonPath, false, true, map.usePolygon, this.onPolygonChange);
    };
    createCircleFromMap = (map: IWorkingAreaMap) => {
        let circleCenter = map.useCircle && map.circleCenter ? map.circleCenter : map.base;
        let circleRadius = map.useCircle && map.circleRadius ? map.circleRadius : 3000;

        return new Circle(
            circleCenter,
            circleRadius,
            false,
            true,
            map.useCircle,
            this.onCircleChange
        );
    };
    createMarkerFromMap = (map: IWorkingAreaMap) => {
        return new Marker(map.base, true, true, this.onMarkerChange);
    };

    usePolygon = () => {
        if (!this.state.workingAreaMaps[this.state.currentIndex].usePolygon) {
            this.setState(
                {
                    workingAreaMaps: this.state.workingAreaMaps.map(
                        (map: IWorkingAreaMap, index: number) => {
                            if (index === this.state.currentIndex) {
                                return merge(map, {
                                    useCircle: false,
                                    polygon: getDefaultPolygon(
                                        this.state.workingAreaMaps[this.state.currentIndex].base
                                    ),
                                    usePolygon: true
                                });
                            }
                            return merge(map, {} as IWorkingAreaMap);
                        }
                    )
                },
                () => {
                    this.props.onMapChange(this.state.workingAreaMaps);
                }
            );
        } else {
            this.setState(
                {
                    workingAreaMaps: this.state.workingAreaMaps.map(
                        (map: IWorkingAreaMap, index: number) => {
                            if (index === this.state.currentIndex) {
                                return merge(map, { usePolygon: false });
                            }
                            return merge(map, {});
                        }
                    )
                },
                () => {
                    this.props.onMapChange(this.state.workingAreaMaps);
                }
            );
        }
    };

    useCircle = () => {
        if (!this.state.workingAreaMaps[this.state.currentIndex].useCircle) {
            this.setState(
                {
                    workingAreaMaps: this.state.workingAreaMaps.map(
                        (map: IWorkingAreaMap, index: number) => {
                            if (index === this.state.currentIndex) {
                                return merge(map, {
                                    useCircle: true,
                                    usePolygon: false,
                                    circleCenter:
                                        this.state.workingAreaMaps[this.state.currentIndex].base
                                });
                            }
                            return merge(map, {});
                        }
                    )
                },
                () => {
                    this.props.onMapChange(this.state.workingAreaMaps);
                }
            );
        } else {
            this.setState(
                {
                    workingAreaMaps: this.state.workingAreaMaps.map(
                        (map: IWorkingAreaMap, index: number) => {
                            if (index === this.state.currentIndex) {
                                return merge(map, {
                                    useCircle: false
                                });
                            }
                            return merge(map, {});
                        }
                    )
                },
                () => {
                    this.props.onMapChange(this.state.workingAreaMaps);
                }
            );
        }
    };

    changeDay(day: number) {
        var result = setupMap(this.state.workingAreaMaps, day, this.getMapHandlers());
        this.setState(
            {
                currentIndex: day,
                workingAreaMaps: result.maps
            },
            () => {
                this.props.onMapChange(this.state.workingAreaMaps);
            }
        );
    }

    render() {
        const marker = this.createMarkerFromMap(
            this.state.workingAreaMaps[this.state.currentIndex]
        );
        return (
            <div>
                <Button
                    name="monday"
                    role="button"
                    bsStyle={this.state.currentIndex === 0 ? 'primary' : 'default'}
                    onClick={() => this.changeDay(0)}
                >
                    {Translate('photographers/modal/form/workareas/button-label/monday')}
                </Button>

                <Button
                    name="tuesday"
                    role="button"
                    bsStyle={this.state.currentIndex === 1 ? 'primary' : 'default'}
                    onClick={() => this.changeDay(1)}
                >
                    {Translate('photographers/modal/form/workareas/button-label/tuesday')}
                </Button>

                <Button
                    name="wednesday"
                    role="button"
                    bsStyle={this.state.currentIndex === 2 ? 'primary' : 'default'}
                    onClick={() => this.changeDay(2)}
                >
                    {Translate('photographers/modal/form/workareas/button-label/wednesday')}
                </Button>

                <Button
                    name="thursday"
                    role="button"
                    bsStyle={this.state.currentIndex === 3 ? 'primary' : 'default'}
                    onClick={() => this.changeDay(3)}
                >
                    {Translate('photographers/modal/form/workareas/button-label/thursday')}
                </Button>

                <Button
                    name="friday"
                    role="button"
                    bsStyle={this.state.currentIndex === 4 ? 'primary' : 'default'}
                    onClick={() => this.changeDay(4)}
                >
                    {Translate('photographers/modal/form/workareas/button-label/friday')}
                </Button>

                <Button
                    name="saturday"
                    role="button"
                    bsStyle={this.state.currentIndex === 5 ? 'primary' : 'default'}
                    onClick={() => this.changeDay(5)}
                >
                    {Translate('photographers/modal/form/workareas/button-label/saturday')}
                </Button>

                <Button
                    name="sunday"
                    role="button"
                    bsStyle={this.state.currentIndex === 6 ? 'primary' : 'default'}
                    onClick={() => this.changeDay(6)}
                >
                    {Translate('photographers/modal/form/workareas/button-label/sunday')}
                </Button>

                <Map
                    settings={[
                        {
                            polygon: this.createPolygonFromMap(
                                this.state.workingAreaMaps[this.state.currentIndex]
                            ),
                            circle: this.createCircleFromMap(
                                this.state.workingAreaMaps[this.state.currentIndex]
                            ),
                            center: marker.Center,
                            marker: marker,
                            entityId: this.props.photographerId
                        }
                    ]}
                    center={marker.Center}
                />

                <Button
                    name="usepolygon"
                    role="button"
                    bsStyle={
                        this.state.workingAreaMaps[this.state.currentIndex].usePolygon
                            ? 'primary'
                            : 'default'
                    }
                    onClick={this.usePolygon}
                >
                    {Translate('photographer/modal/form/workareas/button/use-polygon')}
                </Button>
                <Button
                    name="usecircle"
                    role="button"
                    bsStyle={
                        this.state.workingAreaMaps[this.state.currentIndex].useCircle
                            ? 'primary'
                            : 'default'
                    }
                    onClick={this.useCircle}
                >
                    {Translate('photographer/modal/form/workareas/button/use-circle')}
                </Button>
                <Button
                    name="copytootherdays"
                    role="button"
                    bsStyle={'primary'}
                    onClick={() => {
                        this.props.setDefaultConfigSe360(
                            this.state.workingAreaMaps[this.state.currentIndex]
                        );
                    }}
                >
                    {Translate('styling-user/modal/form/workareas/button/copy-to-other-days/se360')}
                </Button>
            </div>
        );
    }
}

export default WorkAreasForm;
